/**
 * @file seoLanding.js
 * @description Config-as-data for the role×city SEO landing pages (Slice 4) and
 *   shareable copy blocks. Each entry carries genuinely localized facts — nearby
 *   areas, typical schedules, case types, market pay context, and therapist pain
 *   points — so CityRolePage renders substantive, distinct pages rather than
 *   thin city-name swaps (a Google thin-content risk).
 *
 *   IMPORTANT — honesty: ABAcases has very few live cases. Pages must NOT
 *   imply live listings or a cadence of new ones. CityRolePage uses
 *   `honestVolume: true` to render the "No open cases in [Area] right now?
 *   Get alerted…" empty state (only when it knows there are none). Pay ranges are labeled
 *   TYPICAL MARKET context (public BLS/industry ranges), never platform data.
 *
 *   i18n: this file holds the FACTS (area/role data) that drive both English
 *   and Spanish rendering. Bespoke prose facts (pay context, launch notes, pain
 *   points) are stored as i18n KEY references (payContextKey, launchNoteKey,
 *   painPointKeys) — the actual English/Spanish strings live in
 *   src/locales/en.json / es.json under the `cityRole` namespace and are
 *   resolved via t() in CityRolePage.jsx, so the global language selector
 *   controls the visible body content. metaTitle/metaDescription/h1/subtitle
 *   stay English-only here — they feed usePageSeo() (document <title>/<meta
 *   description>/canonical), which is intentionally NOT language-reactive: the
 *   canonical SEO surface (and the prerendered raw HTML Google indexes) stays
 *   English-only for now. There are no /es routes and no hreflang.
 * @module data/seoLanding
 */
import seoRoutes from "./seoRoutes.json";
import seoAreas from "./seoAreas.json";
import { isRouteLive, seoReleaseDate } from "./seoRelease";

// ── Shared building blocks ──────────────────────────────────────────────────

/**
 * Every place a page can be built for: the registry in seoAreas.json (single source of truth, also read by the
 * prebuild guard scripts/check-seo-pages.js). Each area carries real local facts (nearby towns, region, and the
 * i18n keys of its pay, launch note and local facts). See the registry's _comment for the content rules.
 */
const AREAS = seoAreas.areas;
/** The states table (BACB counts, Spanish names, Medicaid program name where it differs). */
const STATES = seoAreas.states;
/** Where the states table's BACB counts come from ({ label, url, readOn }). */
const STATES_SOURCE = seoAreas.statesSource;

/** Role definitions: label, full name, credential line, and typical market pay. */
const ROLES = {
  rbt: {
    roleKey: "rbt",
    label: "RBT",
    full: "Registered Behavior Technician (RBT)",
    credential: "RBT certification",
    // i18n: raw English text lives in en.json/es.json under cityRole.payContext.* /
    // cityRole.credential.* / cityRole.schedule.* — single source of truth,
    // resolved via t() in CityRolePage.jsx. `scheduleKeys` mirrors the first 3
    // entries of `schedules` (the only ones ever rendered — see CityRolePage's
    // .slice(0, 3) card body).
    paySummaryKey: "cityRole.paySummary.rbt",
    payDetailKey: "cityRole.payDetail.rbt",
    credentialKey: "cityRole.credential.rbt",
    schedules: ["after-school (3–7 PM)", "early-intervention mornings", "full-day in-home", "weekend sessions"],
    scheduleKeys: ["cityRole.schedule.rbt1", "cityRole.schedule.rbt2", "cityRole.schedule.rbt3"],
    caseTypes: ["in-home early intervention", "school-based 1:1 support", "center/clinic-based therapy", "after-school behavior programs"],
  },
  bcba: {
    roleKey: "bcba",
    label: "BCBA",
    full: "Board Certified Behavior Analyst (BCBA)",
    credential: "BCBA certification",
    paySummaryKey: "cityRole.paySummary.bcba",
    payDetailKey: "cityRole.payDetail.bcba",
    credentialKey: "cityRole.credential.bcba",
    schedules: ["clinical supervision blocks", "assessment & treatment planning", "in-home and school oversight", "hybrid remote + field"],
    scheduleKeys: ["cityRole.schedule.bcba1", "cityRole.schedule.bcba2", "cityRole.schedule.bcba3"],
    caseTypes: ["case supervision & treatment design", "initial assessments", "parent training", "RBT supervision and oversight"],
  },
  // Behavior technicians (owner 2026-10-20): the entry role with the largest pool, and the job title people search
  // in states like California ("behavior technician jobs"). No role-default pay: every BT page carries its
  // area's own sourced pay (the guard enforces it, Florida included).
  bt: {
    roleKey: "bt",
    label: "BT",
    wordEn: "Behavior Technician (BT)",
    full: "Behavior Technician (BT)",
    credential: "BT training (RBT certification not always required)",
    paySummaryKey: null,
    payDetailKey: null,
    credentialKey: "cityRole.credential.bt",
    schedules: ["after-school (3–7 PM)", "early-intervention mornings", "full-day in-home", "weekend sessions"],
    scheduleKeys: ["cityRole.schedule.bt1", "cityRole.schedule.bt2", "cityRole.schedule.bt3"],
    caseTypes: ["in-home early intervention", "school-based 1:1 support", "center/clinic-based therapy", "after-school behavior programs"],
  },
  // BCaBAs: published only where the state has a real BCaBA workforce (Florida has far more than any other state).
  bcaba: {
    roleKey: "bcaba",
    label: "BCaBA",
    wordEn: "BCaBA",
    full: "Board Certified Assistant Behavior Analyst (BCaBA)",
    credential: "BCaBA certification",
    paySummaryKey: null,
    payDetailKey: null,
    credentialKey: "cityRole.credential.bcaba",
    schedules: ["RBT supervision blocks", "assessment and program updates under a BCBA", "in-home and school oversight", "parent training sessions"],
    scheduleKeys: ["cityRole.schedule.bcaba1", "cityRole.schedule.bcaba2", "cityRole.schedule.bcaba3"],
    caseTypes: ["case supervision under a BCBA", "RBT supervision and oversight", "parent training", "program updates and data review"],
  },
  "aba-cases": {
    roleKey: "aba-cases",
    label: "ABAcases",
    full: "ABA cases (all credentials — BT, RBT, BCaBA, BCBA)",
    credential: "any ABA credential (BT, RBT, BCaBA, or BCBA)",
    paySummaryKey: "cityRole.paySummary.abaCases",
    payDetailKey: "cityRole.payDetail.abaCases",
    credentialKey: "cityRole.credential.abaCases",
    schedules: ["after-school (3–7 PM)", "early-intervention mornings", "full-day in-home", "weekend and flexible hours"],
    scheduleKeys: ["cityRole.schedule.abaCases1", "cityRole.schedule.abaCases2", "cityRole.schedule.abaCases3"],
    caseTypes: ["in-home early intervention", "school-based support", "clinic-based therapy", "supervision roles"],
  },
};

// Per-slug title-suffix overrides (Phase 4B) — everything else uses the
// generic "| ABAcases" suffix; only add an entry here for a page whose
// specified title needs different closing copy.
const TITLE_SUFFIX_OVERRIDES = {
  "aba-cases-florida": "Find Local ABA Case Opportunities",
};

/**
 * Therapist pain points (the brief's language) — reused across every city page.
 * i18n: these are i18n KEYS, not raw text — the English/Spanish strings live in
 * en.json/es.json under cityRole.painPoint1..5, resolved via t() in CityRolePage.jsx.
 */
const PAIN_POINT_KEYS = [
  "cityRole.painPoint1",
  "cityRole.painPoint2",
  "cityRole.painPoint3",
  "cityRole.painPoint4",
  "cityRole.painPoint5",
];

/**
 * BCBA pages get BCBA pain points (caseload size, supervision load, drive time
 * between clients) instead of the RBT-style ones (cancellations eating a
 * paycheck, unpaid sessions) that do not describe a BCBA's week.
 */
const BCBA_PAIN_POINT_KEYS = [
  "cityRole.painPointBcba1",
  "cityRole.painPointBcba2",
  "cityRole.painPointBcba3",
  "cityRole.painPointBcba4",
  "cityRole.painPointBcba5",
];

// ── Per-slug entries (the role×city routes) ─────────────────────────────────
// The canonical slug list lives in seoRoutes.json (the single source of truth
// shared with the prerender + sitemap generator). City pages are the routes
// that carry an area + role; we build the rich page data from those here.

/** [areaKey, roleKey, slug] tuples derived from seoRoutes.json. */
// Only routes released by this build's release date (seoRelease.js): a page scheduled for a later week is not
// routed, linked or listed until the build that releases it.
const RELEASE_DATE = seoReleaseDate();
const SLUGS = seoRoutes.routes
  .filter((r) => r.area && r.role && isRouteLive(r, RELEASE_DATE))
  .map((r) => [r.area, r.role, r.path.replace(/^\//, "")]);

function buildEntry(areaKey, roleKey, slug) {
  const a = AREAS[areaKey];
  const r = ROLES[roleKey];
  const isCases = roleKey === "aba-cases";
  const isState = a.areaType === "state";
  // Multi-county regions (e.g. South Florida) read the same way state hubs do —
  // "South Florida", not "South Florida, FL" — since the area name itself
  // already reads as a place, not a city that needs its state spelled out.
  const isStateOrRegion = isState || a.areaType === "region";

  // "Charlotte, NC" for a city/county; just "North Carolina" / "South Florida"
  // for a state or multi-county-region hub — locFull uses the area's own name
  // (already a complete, unambiguous place name), NOT stateFull, since a region
  // like "South Florida" isn't the same string as its state ("Florida").
  const loc = isStateOrRegion ? a.area : `${a.area}, ${a.state}`;
  const locFull = isStateOrRegion ? a.area : `${a.area}, ${a.stateFull}`;

  // Title/meta follow the brief's "[Role] Jobs & ABA Cases in [City], ST" format: "ABA Cases" is the search phrase
  // here, not the brand (the 2026 rebrand's find-and-replace once turned it into "Jobs & ABAcases", which providers
  // read as a typo; the brand stays in the "| ABAcases" suffix).
  const titleHead = isCases
    ? `ABA Cases in ${loc}`
    : `${r.wordEn || r.label} Jobs & ABA Cases in ${loc}`;

  // Title suffix is "| ABAcases" everywhere except the rare page that needs a
  // more descriptive suffix for its specific search intent (Phase 4B).
  // Long place names (Grand Island, Dallas-Fort Worth) must not push the title past the guard's limit
  // (scripts/lib/seoPageGuards META_TITLE_MAX = 70): drop "& ABA Cases", then the long role word, before overflowing.
  const titleSuffix = TITLE_SUFFIX_OVERRIDES[slug] || "ABAcases";
  const titleHeads = isCases ? [titleHead] : [
    titleHead,
    `${r.wordEn || r.label} Jobs in ${loc}`,
    `${r.label} Jobs & ABA Cases in ${loc}`,
    `${r.label} Jobs in ${loc}`,
  ];
  const metaTitle = titleHeads.map((h) => `${h} | ${titleSuffix}`).find((t) => t.length <= 70)
    || `${titleHeads[titleHeads.length - 1]} | ${titleSuffix}`;
  const metaDescription = isCases
    ? `Find ABA cases in ${locFull} by pay, schedule, and credential. Join free for local case alerts and a built-in hours tracker.`
    : `Find ${r.label} ABA cases in ${locFull} by pay, schedule, and credential. Join free and get local ${r.label} case alerts.`;

  // H1 targets the search phrase directly but does not stuff the brand name:
  // "RBT Jobs in Miami, FL" / "ABA Cases in Miami, FL". The meta title keeps
  // the "& ABA Cases ... | ABAcases" form (titleHead above); the longer
  // descriptive line becomes a subtitle so the H1 isn't diluted.
  const h1 = isCases ? `ABA Cases in ${loc}` : `${r.wordEn || r.label} Jobs in ${loc}`;
  const subtitle = isCases
    ? `ABA cases in ${loc}, matched to your schedule, location, and pay.`
    : `Find ${r.label} cases in ${a.area} that fit your schedule, location, credential, and pay.`;

  // Market pay context: area-specific override (e.g. NC ranges) or the role
  // default. Split into two DISTINCT strings — a short summary for the "Typical pay here"
  // card and a fuller detail for the FAQ — so the same pay sentence is
  // never rendered twice on a page. i18n: KEYs, not raw text.
  // "How do I become a BCBA in <state>?" (state pages and BCBA/BCaBA pages): the answer ends with a sourced licensing
  // fact, the place's own or else its state page's. null = no sourced fact, so the page skips that question.
  const stateHub = Object.values(AREAS).find((x) => x.areaType === "state" && x.state === a.state);
  const licensingKey = (a.localFactKeys && a.localFactKeys.licensing)
    || (stateHub && stateHub.localFactKeys && stateHub.localFactKeys.licensing)
    || null;

  const payOverride = a.payByRole && a.payByRole[roleKey];
  const paySummaryKey = (payOverride && payOverride.summary) || r.paySummaryKey;
  const payDetailKey  = (payOverride && payOverride.detail)  || r.payDetailKey;

  return {
    slug,
    canonical: `/${slug}`,
    areaKey,
    roleKey,
    // facts
    ...a,
    role: r,
    schedules: r.schedules,
    caseTypes: r.caseTypes,
    paySummaryKey,
    payDetailKey,
    licensingKey,
    // Where this page's own pay line comes from ({ by, url, citeKey? }); null = no linkable source, so the page
    // shows no "at a glance" box (CityAtAGlance) and keeps its "Typical pay here" card.
    paySource: (payOverride && payOverride.source) || null,
    // Extra "at a glance" lines this area opts into (credential, workforce, employment); default is pay only.
    glanceExtras: (a.glanceExtras && a.glanceExtras[roleKey]) || [],
    // The area's own extra FAQs for this role (seoAreas.json faqByRole).
    extraFaqs: (a.faqByRole && a.faqByRole[roleKey]) || [],
    // BCaBAs supervise and run caseloads like BCBAs, so they share the analyst pain points; BTs share the RBT ones.
    painPointKeys: roleKey === "bcba" || roleKey === "bcaba" ? BCBA_PAIN_POINT_KEYS : PAIN_POINT_KEYS,
    honestVolume: true,
    isStateHub: isState,
    // "Charlotte, NC" / "North Carolina" — used where area+state read together.
    locLabel: loc,
    // NC launch pages only (null elsewhere): expansion note + breadcrumb parent hub.
    launchNoteKey: a.launchNoteKey || null,
    launchTitleKey: a.launchTitleKey || null,
    parentHub: a.parentHub || null,
    // English-only copy — used for document <title>/<meta description>/canonical
    // (usePageSeo) ONLY. The raw prerendered SEO surface stays English regardless
    // of the visible-body language toggle (see CityRolePage.jsx for the
    // Spanish-reactive H1/subtitle/CTA, which are computed independently via t()
    // rather than from these fields).
    metaTitle,
    metaDescription,
    h1,
    subtitle,
  };
}

export const SEO_LANDINGS = SLUGS.map(([areaKey, roleKey, slug]) => buildEntry(areaKey, roleKey, slug));

/** All landing slugs (used for routing + sitemap + internal-link hub). */
export const SEO_LANDING_SLUGS = SEO_LANDINGS.map((e) => e.slug);

/** Lookup a landing entry by slug (no leading slash). */
export function getLanding(slug) {
  const clean = (slug || "").replace(/^\/+/, "").replace(/\/+$/, "");
  return SEO_LANDINGS.find((e) => e.slug === clean) || null;
}

/**
 * Related landing pages for in-body internal linking (hub-and-spoke / de-orphaning).
 * Returns up to `max` sibling entries, ordered: same place / other roles first
 * (so a city's BCBA / aba-cases pages get linked and stop being
 * orphans), then same-role pages in the same hub family (sibling cities, the hub
 * itself, or — for a hub — its children), the higher `linkWeight` places first. Capped at 6 to stay well under a
 * link-farm threshold. Used by CityRolePage to render crawlable sibling links.
 * @param {string} slug landing slug (no leading slash)
 * @param {number} [max=6]
 * @returns {Object[]} landing entries
 */
export function relatedLandings(slug, max = 6) {
  const e = getLanding(slug);
  if (!e) return [];
  const hubSlug = e.parentHub && e.parentHub.slug;
  const others = SEO_LANDINGS.filter((x) => x.slug !== e.slug);
  const sameCityOtherRole = others.filter((x) => x.areaKey === e.areaKey);
  const sameRoleSameFamily = others.filter((x) =>
    x.areaKey !== e.areaKey &&
    x.roleKey === e.roleKey &&
    (
      (hubSlug && x.parentHub && x.parentHub.slug === hubSlug) || // sibling cities under my hub
      x.slug === hubSlug ||                                       // my parent hub page
      (e.isStateHub && x.state === e.state) ||                    // I'm a hub → my children
      (x.parentHub && x.parentHub.slug === e.slug)                // pages whose hub is me
    )
  );
  // When a family has more pages than slots, the heavier-weighted places (seoAreas.json "linkWeight", set from
  // Search Console demand) get the slots first. Array.sort is stable, so equal weights keep registry order.
  const byWeight = (a, b) => (b.linkWeight || 0) - (a.linkWeight || 0);
  const seen = new Set();
  const out = [];
  for (const x of [...sameCityOtherRole, ...[...sameRoleSameFamily].sort(byWeight)]) {
    if (seen.has(x.slug)) continue;
    seen.add(x.slug);
    out.push(x);
    if (out.length >= max) break;
  }
  return out;
}

export { AREAS, STATES, STATES_SOURCE, ROLES, PAIN_POINT_KEYS, BCBA_PAIN_POINT_KEYS };
